/**
 * What a text area cannot draw inside its own text: where a dictation will land, the transcribing
 * mark at that place, and the dictated words entering from blur as Buddy's own words do (board
 * notes, „Inserted"). A copy of the field's text with the field's exact type and wrapping, laid over
 * it; the text area stays the field, keeps the caret and takes the keys.
 *
 * The text before the mark is drawn transparent, so only the mark shows over the field's own ink.
 * For the words, the field veils its text for as long as they take, and this draws all of it.
 */

import clsx from 'clsx';
import type { CSSProperties } from 'react';

import messages from './Messages.module.css';
import surfaces from './surfaces.module.css';
import styles from './Composer.module.css';

export type MirrorContent =
  /** The text is already in the field; a pulsing mark stands where the dictation will land. */
  | { readonly kind: 'marker'; readonly value: string; readonly at: number }
  /** „przepisuję…" shimmering at that place while the server works. */
  | { readonly kind: 'ghost'; readonly value: string; readonly at: number; readonly label: string }
  /** The words just inserted at `start`, entering one after another. */
  | { readonly kind: 'words'; readonly value: string; readonly start: number; readonly insert: string };

export interface DictationMirrorProps {
  readonly content: MirrorContent;
  /** The field's own type: the hero's or the dock's. */
  readonly fieldClass: string | undefined;
  /** The field's scroll, so a long text lines up with what it covers. */
  readonly scrollTop: number;
  /** The last word has entered: the field shows its own text again. */
  readonly onEntered: () => void;
}

export function DictationMirror({ content, fieldClass, scrollTop, onEntered }: DictationMirrorProps) {
  const shift: CSSProperties = { transform: `translateY(${String(-scrollTop)}px)` };
  if (content.kind !== 'words') {
    return (
      <span className={clsx(styles.field, fieldClass, styles.mirror)} aria-hidden="true">
        <span style={shift} className={styles.mirrorText}>
          <span className={styles.mirrorHidden}>{content.value.slice(0, content.at)}</span>
          {content.kind === 'marker' ? <span className={styles.marker} /> : <span className={clsx(surfaces.shimmer, styles.ghost)}>{content.label}</span>}
        </span>
      </span>
    );
  }
  const end = content.start + content.insert.length;
  const parts = content.insert.split(/(\s+)/);
  const isWord = (part: string) => /\S/.test(part);
  const last = parts.findLastIndex(isWord);
  return (
    <span className={clsx(styles.field, fieldClass, styles.mirror)} aria-hidden="true">
      <span style={shift} className={styles.mirrorText}>
        {content.value.slice(0, content.start)}
        {parts.map((part, index) => {
          if (!isWord(part)) return part;
          const step = parts.slice(0, index).filter(isWord).length;
          return (
            <span
              key={index}
              className={messages.word}
              style={{ '--word-index': step } as CSSProperties}
              onAnimationEnd={index === last ? onEntered : undefined}
            >
              {part}
            </span>
          );
        })}
        {content.value.slice(end)}
      </span>
    </span>
  );
}
